import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import client from 'app/client';

export interface AbpApplicationConfiguration {
  auth: {
    /** Toutes les permissions DÉFINIES par l'application (true pour tous, même anonyme). */
    policies: Record<string, boolean>;
    /** Permissions effectivement ACCORDÉES à l'utilisateur courant. */
    grantedPolicies: Record<string, boolean>;
  };
  currentUser: {
    isAuthenticated: boolean;
    id?: string;
    userName?: string;
    email?: string;
    roles: string[];
  };
  setting: {
    values: Record<string, string>;
  };
  localization: {
    currentCulture: {
      name: string;
      displayName: string;
    };
  };
}

export interface AbpState {
  configuration: AbpApplicationConfiguration | null;
  initialized: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AbpState = {
  configuration: null,
  initialized: false,
  loading: false,
  error: null
};

const fetchAbpConfiguration = async (): Promise<AbpApplicationConfiguration> => {
  const response = await client.get('/abp/application-configuration');
  return response.data;
};

export const initializeAbp = createAsyncThunk(
  'abp/initialize',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchAbpConfiguration();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(message);
    }
  }
);

export const refreshAbpPermissions = createAsyncThunk(
  'abp/refreshPermissions',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchAbpConfiguration();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(message);
    }
  }
);

const abpSlice = createSlice({
  name: 'abp',
  initialState,
  reducers: {
    resetAbpError: (state) => {
      state.error = null;
    },
    forceReinitialize: (state) => {
      state.initialized = false;
      state.loading = false;
      state.error = null;
      state.configuration = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAbp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeAbp.fulfilled, (state, action) => {
        state.loading = false;
        state.configuration = action.payload;
        state.initialized = true;
        state.error = null;
      })
      .addCase(initializeAbp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.initialized = false;
      })
      .addCase(refreshAbpPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshAbpPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.configuration = action.payload;
        state.error = null;
      })
      .addCase(refreshAbpPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const {
  resetAbpError,
  forceReinitialize
} = abpSlice.actions;

export default abpSlice.reducer;
